// OrderData 前端类型定义，对应后端 mongodb.OrderData（caligula-api/internal/model/entity/mongodb/order_data.go）

export interface TenancyData {
  /** 起租时间（原始字段，通常为字符串时间） */
  start_time: string;
  /** 到期时间（原始字段，通常为字符串时间） */
  end_time: string;
  /** 总天数 */
  total_days: number;
}

export interface AccessoryData {
  /** 配件名称 */
  accessory_name: string;
  /** 配件类型 */
  accessory_type: string;
  /** 配件单价（字符串，部分来源会携带格式化） */
  accessory_price: string;
  /** 数量 */
  quantity: string;
}

export interface OrderTags {
  /** 是否直付通订单 */
  zftOrder: boolean;
  /** 支付方式 */
  payType: string;
  /** 是否为管控订单 */
  cancelControl: boolean;
  /** 续租订单 */
  renewalOrder: boolean;
  /** 极优免审必发 */
  casualExcellentOrder: boolean;
  /** 预授权订单 */
  preAuthorization: boolean;
  /** 转化订单（公域池） */
  isPubDomainPoolOrder: boolean;
  /** 系统补订单 */
  systemCompensateOrder: boolean;
}

export interface LogisticsInfo {
  /** 寄出方式 */
  sendType: number;
  /** 归还方式 */
  returnType: number;
  /** 物流单号 */
  logisticNumber?: string;
  /** 物流名称 */
  logisticName?: string;
  /** 发货日期 */
  sendDate?: string;
}

export const LogisticsNameMap: Record<string, string> = {
  // 常用快递
  "SF":   "顺丰速运",
  "ZTO":  "中通快递",
  "YTO":  "圆通速递",
  "YD":   "韵达快递",
  "STO":  "申通快递",
  "JTSD": "极兔速递",
  "YZPY": "邮政快递包裹",
  "EMS":  "EMS",
  "JD":   "京东快递",
  "DBL":  "德邦快递",

  // 其他快递
  "HTKY":    "百世快递",
  "DNWL":    "丹鸟",
  "ANE":     "安能物流",
  "ZJS":     "宅急送",
  "FAST":    "快捷快递",
  "QFKD":    "全峰快递",
  "BSHT":    "百世快运",
  "YUNDA56": "韵达快运",
  "SURE":    "速尔快递",
  "XFWL":    "信丰物流",
  "YMDD":    "优速快递",

  // 国际快递
  "FEDEX":     "联邦快递",
  "UPS":       "UPS",
  "DHL":       "DHL",
  "TNT":       "TNT",
  "ARAMEX":    "Aramex",
  "DPEX":      "DPEX",
  "COE":       "COE",
  "USPS":      "USPS",
  "JAPANPOST": "日本邮政",
};

export const SendTypeMap: Record<number, string> = {
  1: "包邮",
  2: "到付",
  3: "上门安装",
  4: "到店自取",
  5: "统一运费",
  6: "运费模板",
  7: "租满XX天寄出包邮",
  8: "上门自提",
  9: "同城闪送",
};

export const ReturnTypeMap: Record<number, string> = {
  1: "包邮",
  2: "自付",
  3: "无需归还",
  4: "租满XX天归还包邮",
};

export interface OrderStatCount {
  /** 公司总下单次数 */
  companyOrderCount: number;
  /** 公司成功下单次数 */
  companySuccessOrderCount: number;
}

export interface OrderData {
  // ==== Base 基础信息（来源于订单本体）====
  /** 文档唯一 ID（MongoDB ObjectID 字符串） */
  id: string;
  /** 订单号 */
  orderNo: string;
  /** 店铺 ID */
  storeId: number;
  /** 订单状态（枚举见 OrderStatus / ORDER_STATUS_LABEL_MAP） */
  orderStatus: number;
  /** 订单创建时间（ISO 字符串） */
  orderCreatedAt: string;

  // ==== Tags 标签信息 ====
  /** 应用端类型（order.main.tags.origin） */
  appType: string;
  /** 考核订单类型（发货看板 AssessDimenSign） */
  assessmentType?: string;
  /** 归还策略（return_at_end/return_anytime） */
  leaseReturnPolicy?: string;
  /** 最少租用月份（如 sku 文案中的“3个月起”） */
  minimumLeaseMonths?: number;
  /** 订单展示标签集合 */
  tags?: OrderTags;

  /** 是否支持续租 */
  supportRenewal?: boolean;
  /** 续租原订单号 */
  renewalOriginOrderNo?: string;
  /** 结算比例 */
  settlementRatio?: string;
  /** 续租每期费率 */
  buyoutRate?: string;
  /** 物流信息 */
  logisticsInfo?: LogisticsInfo;
  /** 内部订单相关统计 */
  orderStatCount?: OrderStatCount;

  // ==== 用户与地址 ====
  /** 省份 */
  province: string;
  /** 城市 */
  city: string;
  /** 区县 */
  district: string;
  /** 用户地址（可能已拼接为完整地址） */
  userAddress: string;
  /** 用户认证姓名 */
  userAuthName: string;
  /** 用户手机号 */
  userPhone: string;
  /** 用户注册手机号 */
  userRegisterPhone: string;
  /** 用户身份证号 */
  userAuthIdCard: string;
  /** 人脸识别状态 */
  faceRecognitionStatus: string;
  /** 用户年龄 */
  userAge: number;
  /** 用户性别 */
  userSex: string;

  // ==== 租期基础 ====
  /** 租期开始时间（北京时间，ISO 字符串） */
  tenancyStartTime?: string;
  /** 原始租期数据 */
  tenancyData?: TenancyData;

  // ==== 发货与财务（源自订单本体） ====
  /** 订单原始租金（order.main.rent_info.origin_rent） */
  originRent: number;
  /** 首期租金（order.main.rent_info.first_rent） */
  firstRent: number;
  /** 账单价格 intsallment_price */
  installmentPrice: number;
  /** 需要支付的租金（order.main.rent_info.rental_need_pay） */
  rentalNeedPay: number;
  /** 押金金额 */
  depositValue: number;
  /** 信用评估额度（order.main.deposit_info.credit_evaluatie_quota） */
  creditEvaluatieQuota: number;
  /** 商品价值（使用押金金额作为估算） */
  productValue: number;
  /** 到期购买价（order.main.order_pay_info.current_purchase_price） */
  buyoutPrice: number;
  /** 退款累计金额（order.main.order_pay_info.already_refund） */
  refundAmount: number;
  /** 已冻结押金 */
  alreadyFrozen: number;
  /** 已支付租金（扣除退款） */
  hadPayRental: number;
  /** 预计回收金额（押金-已付租金-已冻结押金） */
  expectedRecoveryAmount: number;
  /** 支付成功时间（order.main.order_pay_info.pay_success_date） */
  paySuccessDate: string;



  // ==== Product 商品与设备信息（来源产品或订单） ====
  /** 商品代码 URL（用于关联 products.sourceId） */
  codeUrl: string;
  /** 品牌 ID（来自产品库） */
  brandId?: string;
  /** 商品标题/方案标题 */
  productTitle: string;
  /** 品牌名称（苹果/安卓/其他） */
  brandName: string;
  /** 型号（SKU） */
  model: string;
  /** 套餐信息 */
  skuName: string;
  /** 是否二手手机（来自 spu_info） */
  isOldMobilePhone: boolean;
  /** 商品图片 */
  spuCoverUrl: string;
  /** 配件清单（order.main.spu_info.accessory_data） */
  accessoryData: AccessoryData[];
  /** 商品来源（orders/products） */
  productSource: string;
  /** 货源（s.deviceInfo.warehouse_deliver_imei.warehouse_source） */
  warehouseSource: number;
  /** 设备类型（全新苹果有锁/二手苹果有锁/全新安卓有锁） */
  deviceType: string;
  /** 是否锁机标记 */
  lockDevice: boolean;

  /** 商品数量 */
  quantity: number;

  // ==== Risk 风控信息 ====
  /** 是否盲发订单 */
  isBlindShip: boolean;
  /** 风控定性（通过/不通过） */
  riskQualitative: string;
  /** 风控结果 */
  riskResult?: string;
  /** 风控内容 */
  riskContent?: string;

  // ==== Bills 分期与违约统计 ====
  /** 当前期数（基于分期明细计算） */
  currentPeriod: number;
  /** 是否进入 M1（第二期及以后） */
  isM1Order: boolean;
  /** 是否处于逾期中（当前或上一期未支付） */
  isOverduingOrder: boolean;
  /** 是否有逾期记录（历史期已逾期） */
  isOverduedOrder: boolean;
  /** 应收分期总数 */
  receivableInstallmentPeriodsCount: number;
  /** 已逾期分期数量 */
  overduedInstallmentPeriodsCount: number;
  /** 逾期中分期数量 */
  overduingInstallmentPeriodsCount: number;
  /** 已逾期分期账单金额汇总 */
  overduedInstallmentPeriodsAmount: string;
  /** 已处理逾期金额汇总 */
  handledOverduedInstallmentPeriodsAmount: string;
  /** 已处理逾期分期数量 */
  handledOverduedInstallmentPeriodsCount: number;
  /** 逾期中分期账单金额汇总 */
  overduingInstallmentPeriodsAmount: string;
  /** 坏账分期数量（预计难以回收） */
  badDebtCount: number;
  /** 应收租金（分期账单金额汇总） */
  receivableRental: number;
  /** 实收租金（线下支付或支付完成实际支付金额汇总） */
  receivedRental: number;
  /** 最大逾期天数（从最早逾期开始到当前） */
  overdueDays: number;
  /** 最早发生逾期的期数 */
  earliestOverduePeriod: number;
  /** 最早逾期开始日期 */
  overdueStartDate: string;

  // ==== Meta 文档元信息 ====
  /** 文档创建时间 */
  createdAt: string;
  /** 文档更新时间 */
  updatedAt: string;

  // ==== 兼容字段（历史/其他接口返回）====
  /** 兼容：是否直付通订单（优先使用 tags.zftOrder） */
  zftOrder?: boolean;
  /** 兼容：支付方式（优先使用 tags.payType） */
  payType?: string;
  /** 兼容：是否为管控订单（优先使用 tags.cancelControl） */
  cancelControl?: boolean;
  /** 兼容：用于部分统计场景的订单日期（非 mongodb.OrderData 原生字段） */
  orderDate?: string;
  /** 兼容：是否已发货（非 mongodb.OrderData 原生字段） */
  isShipped?: boolean;
  /** 兼容：发货时间 */
  shipAt?: string;
  /** 兼容：签收时间 */
  takeAt?: string;
}

// 与后端 mongodb.OrderStatus 一致的订单状态枚举
export enum OrderStatus {
  NoPay = 1, // 待付款
  WaitSend = 2, // 待发货
  WaitReceive = 3, // 待收货
  WaitReturn = 4, // 待归还
  Returning = 5, // 归还中
  Returned = 6, // 已归还
  WaitRisk = 7, // 待风控
  CreditAuth = 8, // 信用授权
  Buyout = 19, // 买断
  Complete = 20, // 完成
  CloseUser = 21, // 用户关闭
  CloseServer = 22, // 服务器关闭
  CloseSystem = 23, // 系统关闭
  HadClosedOrder = 24, // 已关闭订单
  Deleted = 30, // 已删除
  Expire = 31, // 已过期
  Quick = 32, // 快速
  WaitRiskClose = 33, // 待风控关闭
  WaitPayClose = 34, // 待付款关闭
  WaitShippedClose = 35, // 待发货关闭
}

// 前端展示中文描述的映射
export const ORDER_STATUS_LABEL_MAP: Record<number, string> = {
  [OrderStatus.NoPay]: '待付款',
  [OrderStatus.WaitSend]: '待发货',
  [OrderStatus.WaitReceive]: '待收货',
  [OrderStatus.WaitReturn]: '待归还',
  [OrderStatus.Returning]: '归还中',
  [OrderStatus.Returned]: '已归还',
  [OrderStatus.WaitRisk]: '待风控',
  [OrderStatus.CreditAuth]: '信用授权',
  [OrderStatus.Buyout]: '买断',
  [OrderStatus.Complete]: '完成',
  [OrderStatus.CloseUser]: '用户关闭',
  [OrderStatus.CloseServer]: '租赁商关闭',
  [OrderStatus.CloseSystem]: '系统关闭',
  // [OrderStatus.HadClosedOrder]: '已关闭订单',
  // [OrderStatus.Deleted]: '已删除',
  // [OrderStatus.Expire]: '已过期',
  // [OrderStatus.Quick]: '快速',
  // [OrderStatus.WaitRiskClose]: '待风控关闭',
  // [OrderStatus.WaitPayClose]: '待付款关闭',
  // [OrderStatus.WaitShippedClose]: '待发货关闭',
};

// 获取订单状态中文描述
export function getOrderStatusLabel(status?: number): string {
  if (status === undefined || status === null) return '-';
  return ORDER_STATUS_LABEL_MAP[status] ?? String(status);
}
